/**
 * 图片处理工具函数
 */

/**
 * 压缩Base64图片
 * @param base64 Base64字符串
 * @param maxWidth 最大宽度
 * @param maxHeight 最大高度
 * @param quality 压缩质量 (0-1)
 * @returns 压缩后的Base64字符串
 */
export const compressBase64Image = (
  base64: string,
  maxWidth: number = 1920,
  maxHeight: number = 1080,
  quality: number = 0.8
): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      // 创建图片对象
      const img = new Image();
      
      img.onload = () => {
        try {
          // 创建canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('无法获取Canvas上下文'));
            return;
          }
          
          // 计算压缩后的尺寸
          let { width, height } = img;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          // 设置canvas尺寸
          canvas.width = width;
          canvas.height = height;
          
          // 绘制压缩后的图片
          ctx.drawImage(img, 0, 0, width, height);
          
          // 转换为Base64
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };
      
      // 设置图片源
      img.src = base64;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * 检查图片大小
 * @param base64 Base64字符串
 * @returns 图片大小（字节）
 */
export const getBase64ImageSize = (base64: string): number => {
  // 移除data:image/...;base64,前缀
  const base64Data = base64.split(',')[1] || base64;
  // Base64编码后的字符串长度 * 3/4 就是原始字节数
  return Math.round((base64Data.length * 3) / 4);
};

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的大小字符串
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 预加载图片
 * @param src 图片源
 * @returns Promise<boolean> 加载是否成功
 */
export const preloadImage = (src: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
};

